import {useCallback, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {isAxiosError} from "axios";
import {ArrowLeft} from "lucide-react";
import {v4 as UUID} from "uuid";
import type {Dungeon} from "@/dungeons/model/Dungeon.ts";
import {
    dungeonSchema, newDungeonLevel, toSaveDungeonBasicsRequest,
    type DungeonFormInput, type DungeonFormOutput, type SaveDungeonBasicsRequest,
} from "./DungeonFormSchema.ts";
import {Form} from "@/components/ui/form.tsx";
import {Button} from "@/components/ui/button.tsx";
import SaveButton, {type SaveState} from "@/components/buttons/SaveButton.tsx";
import ConfirmDialog from "@/components/forms/ConfirmDialog.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import DungeonBasicForm from "./DungeonBasicForm.tsx";
import {changedMapImages} from "./changedMapImages.ts";
import {notifyError} from "@/api/ErrorNotification.ts";

export default function DungeonBasicsForm({dungeon, onSubmit, onSaved, onCancel, onDirtyChange}: {
    dungeon?: Dungeon;
    onSubmit: (request: SaveDungeonBasicsRequest) => Promise<Dungeon>;
    onSaved: (dungeon: Dungeon) => void;
    onCancel: () => void;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [error, setError] = useState("");
    const [uploading, setUploading] = useState<Set<string>>(new Set());
    const [committers, setCommitters] = useState<Map<string, () => void>>(new Map());
    const [pendingConfirmation, setPendingConfirmation] = useState<DungeonFormOutput>();
    const form = useForm<DungeonFormInput, unknown, DungeonFormOutput>({
        resolver: zodResolver(dungeonSchema),
        mode: "onBlur",
        defaultValues: dungeon ?? {id: UUID(), name: "", coverImage: null, levels: [newDungeonLevel(0)]},
    });

    useEffect(() => onDirtyChange(form.formState.isDirty), [form.formState.isDirty, onDirtyChange]);
    const registerCommit = useCallback((id: string, commit?: () => void) => {
        setCommitters(previous => {
            const next = new Map(previous);
            if (commit) next.set(id, commit);
            else next.delete(id);
            return next;
        });
    }, []);
    const registerUploading = useCallback((id: string, isUploading: boolean) => {
        setUploading(previous => {
            const next = new Set(previous);
            if (isUploading) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    const persist = async (data: DungeonFormOutput) => {
        setSaveState("saving");
        setError("");
        try {
            const saved = await onSubmit(toSaveDungeonBasicsRequest(data));
            committers.forEach(commit => commit());
            form.reset(saved);
            setSaveState("success");
            onSaved(saved);
        } catch (reason) {
            setSaveState("error");
            if (isAxiosError(reason) && reason.response?.status === 409) {
                setError("Der Dungeon wurde zwischenzeitlich geändert. Lade ihn neu, bevor du erneut speicherst.");
            } else notifyError(reason, "Der Dungeon konnte nicht gespeichert werden.");
        }
    };
    const onValid = (data: DungeonFormOutput) => {
        if (uploading.size > 0) return;
        if (changedMapImages(data, dungeon).length > 0) setPendingConfirmation(data);
        else void persist(data);
    };
    const onInvalid = () => setError("Bitte überprüfe die markierten Eingaben.");
    const changedLevels = pendingConfirmation ? changedMapImages(pendingConfirmation, dungeon) : [];

    return <main className="min-h-0 grow overflow-auto p-3">
        <ConfirmDialog open={Boolean(pendingConfirmation)} onOpenChange={open => {
            if (!open) setPendingConfirmation(undefined);
        }} title="Neue Level-Karte speichern?"
        description={`Die Karte für ${changedLevels.map(level => level.name).join(", ")} wird ersetzt.${changedLevels.some(level => level.dimensionsChanged) ? " Mindestens ein Bild hat andere Pixelmaße." : ""} Grid und Geometrie bleiben unverändert; prüfe danach ihre Ausrichtung im Editor.`}
        confirm="Trotzdem speichern" onConfirm={() => {
            const data = pendingConfirmation;
            setPendingConfirmation(undefined);
            if (data) void persist(data);
        }}/>
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onValid, onInvalid)} className="mx-auto max-w-5xl space-y-2">
                <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="-ml-2 text-muted-foreground">
                    <ArrowLeft/> {dungeon ? "Zurück zum Dungeon" : "Alle Dungeons"}
                </Button>
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight">{dungeon ? `${dungeon.name} bearbeiten` : "Neuer Dungeon"}</h1>
                    </div>
                    <SaveButton saveState={saveState} disabled={!form.formState.isDirty || uploading.size > 0 || saveState === "saving"}/>
                </div>
                {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
                <HiddenField name="id"/>
                <DungeonBasicForm registerCommit={registerCommit} registerUploading={registerUploading}
                    onUploadStart={() => setError("")}/>
            </form>
        </Form>
    </main>;
}
